"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef, useState, type FormEvent } from "react";
import type { Lang } from "@/lib/lang";
import { COPY } from "./copy";
import { ArrowIcon, Overline, Reveal, useFillOnArrival } from "./ui";

/* ── Clubs ───────────────────────────────────────────────────────────────── */

const CLUB_ICONS = [
  <path key="a" d="M4 5h7v6H4zM13 5h7v6h-7zM4 13h7v6H4zM13 13h7v6h-7z" />,
  <g key="b"><circle cx="9" cy="8" r="3" /><path d="M3.5 19c.9-3 3-4.6 5.5-4.6s4.6 1.6 5.5 4.6" /><path d="M16 5.5a3 3 0 0 1 0 5.6M18 14.6c1.4.6 2.3 1.9 2.8 4" /></g>,
  <g key="c"><path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20z" /><path d="M14 3.5V8h4M9 12h6M9 15.5h6" /></g>,
  <g key="d"><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.4-4.4" /><path d="M8.5 11h5M11 8.5v5" /></g>,
];

export function Clubs({ lang }: { lang: Lang }) {
  const k = COPY.clubs;
  return (
    <section id="clubs" className="sn-grain relative isolate scroll-mt-16 overflow-hidden py-28 sm:py-36 lg:py-44">
      <div className="absolute inset-0 -z-10">
        <Image src="/landing/moment-pass.webp" alt="" fill sizes="100vw" className="object-cover" style={{ objectPosition: "50% 40%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #05070d 0%, rgba(5,7,13,.78) 18%, rgba(5,7,13,.86) 70%, #05070d 100%)" }} />
      </div>
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
        <Reveal delay={0.06}><h2 className="sn-wide mt-4 max-w-[16ch] text-[clamp(32px,5vw,74px)]">{k.title[lang]}</h2></Reveal>
        <Reveal delay={0.12}><p className="sn-body mt-6">{k.body[lang]}</p></Reveal>

        <div className="mt-14 grid gap-px overflow-hidden rounded-[28px] sm:grid-cols-2 lg:grid-cols-4" style={{ background: "var(--sn-hair)" }}>
          {k.items.map((it, i) => (
            <Reveal key={it.title.en} delay={0.06 * i} className="h-full">
              <div className="flex h-full flex-col gap-4 p-7 sm:p-8" style={{ background: "rgba(10,13,21,.88)", backdropFilter: "blur(12px)" }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: "rgba(255,255,255,.88)" }}>
                  {CLUB_ICONS[i]}
                </svg>
                <p className="text-[18px] font-semibold leading-snug">{it.title[lang]}</p>
                <p className="text-[14.5px] leading-relaxed" style={{ color: "var(--sn-fg2)" }}>{it.body[lang]}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-[14.5px]" style={{ color: "var(--sn-fg2)" }}>{k.families[lang]}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ── How it works ────────────────────────────────────────────────────────── */

export function Steps({ lang }: { lang: Lang }) {
  const k = COPY.steps;
  const ref = useRef<HTMLOListElement>(null);
  useFillOnArrival(ref, { delay: 0.15, stagger: 0.22, duration: 1 });
  return (
    <section className="relative py-24 sm:py-32 lg:py-36" aria-labelledby="sn-steps-title">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
        <Reveal delay={0.06}><h2 id="sn-steps-title" className="sn-wide mt-4 text-[clamp(32px,4.6vw,66px)]">{k.title[lang]}</h2></Reveal>
        <ol ref={ref} className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {k.items.map((it, i) => (
            <li key={it.title.en} className="flex flex-col">
              <div className="relative h-[2px] overflow-hidden rounded-full" style={{ background: "var(--sn-hair)" }}>
                <span data-fill className="absolute inset-0 rounded-full" style={{ background: "var(--sn-gold)", transformOrigin: "left" }} />
              </div>
              <span className="sn-num mt-6 text-[15px]" style={{ color: "var(--sn-gold)" }}>{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-3 text-[20px] font-semibold leading-snug">{it.title[lang]}</p>
              <p className="mt-2 text-[15px] leading-relaxed" style={{ color: "var(--sn-fg2)" }}>{it.body[lang]}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ── Contact ─────────────────────────────────────────────────────────────── */

type Status = "idle" | "sending" | "sent" | "failed";

function Field({ label, ...input }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[13px] font-semibold" style={{ color: "var(--sn-fg2)" }}>{label}</span>
      <input
        {...input}
        className="h-12 rounded-[14px] px-4 text-[15.5px] text-white outline-none transition-colors placeholder:text-white/30 focus:border-[var(--sn-gold)]"
        style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.14)" }}
      />
    </label>
  );
}

export function Contact({ lang }: { lang: Lang }) {
  const k = COPY.contact;
  const [form, setForm] = useState({ name: "", club: "", email: "", phone: "" });
  const [status, setStatus] = useState<Status>("idle");
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Netlify Forms: the "club-interest" form is declared in public/__forms.html
  // so Netlify registers it at build time; this posts the same fields to it.
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (status === "sending" || status === "sent") return;
    setStatus("sending");
    const data: Record<string, string> = { "form-name": "club-interest", "bot-field": "", ...form };
    try {
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: Object.entries(data).map(([key, v]) => `${encodeURIComponent(key)}=${encodeURIComponent(v.trim())}`).join("&"),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      setForm({ name: "", club: "", email: "", phone: "" });
    } catch {
      setStatus("failed");
      const body = encodeURIComponent(`${form.name}\n${form.club}\n${form.email}\n${form.phone}`);
      window.location.href = `mailto:support@draffte.com?subject=${encodeURIComponent("Draffte demo — " + form.club)}&body=${body}`;
    }
  };

  return (
    <section id="contact" className="sn-grain relative isolate scroll-mt-16 overflow-hidden pb-24 pt-32 sm:pb-32 sm:pt-44">
      <div className="absolute inset-0 -z-10">
        <Image src="/landing/closer.webp" alt={k.alt[lang]} fill sizes="100vw" className="object-cover" style={{ objectPosition: "50% 45%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #05070d 0%, rgba(5,7,13,.55) 30%, rgba(5,7,13,.82) 62%, #05070d 100%)" }} />
      </div>
      <div className="mx-auto grid max-w-[1360px] gap-14 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:items-end lg:gap-20">
        <div>
          <Reveal><Overline gold>{k.over[lang]}</Overline></Reveal>
          <Reveal delay={0.06}><h2 className="sn-wide mt-4 max-w-[14ch] text-[clamp(36px,6vw,92px)]">{k.title[lang]}</h2></Reveal>
          <Reveal delay={0.12}><p className="sn-body mt-6" style={{ color: "rgba(255,255,255,.78)" }}>{k.body[lang]}</p></Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 grid max-w-[34rem] gap-px overflow-hidden rounded-[22px] sm:grid-cols-2" style={{ background: "var(--sn-hair)" }}>
              {[
                { t: k.playersTitle, c: k.playersCta, href: "/signup" },
                { t: k.coachesTitle, c: k.coachesCta, href: "/signup/coach" },
              ].map((x) => (
                <Link key={x.href} href={x.href} className="group flex flex-col gap-1 p-5 transition-colors hover:bg-white/[0.08]" style={{ background: "rgba(10,13,21,.82)" }}>
                  <span className="sn-over !text-[10px]">{x.t[lang]}</span>
                  <span className="flex items-center justify-between gap-3 text-[15.5px] font-semibold">
                    {x.c[lang]} <ArrowIcon className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-[14px]" style={{ color: "var(--sn-fg2)" }}>
              {k.already[lang]}{" "}
              <Link href="/signin" className="font-semibold text-white underline-offset-4 hover:underline">{k.signIn[lang]}</Link>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="sn-glass flex flex-col gap-4 rounded-[28px] p-6 sm:p-8" name="club-interest" aria-label={k.over[lang]}>
            <input type="text" name="bot-field" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={k.name[lang]} name="name" autoComplete="name" required value={form.name} onChange={set("name")} />
              <Field label={k.club[lang]} name="club" autoComplete="organization" required value={form.club} onChange={set("club")} />
            </div>
            <Field label={k.email[lang]} name="email" type="email" autoComplete="email" inputMode="email" required value={form.email} onChange={set("email")} />
            <Field label={k.phone[lang]} name="phone" type="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} />
            <button type="submit" className="sn-gold-btn mt-2 w-full disabled:opacity-60" disabled={status === "sending"}>
              {status === "sending" ? k.sending[lang] : k.submit[lang]} {status === "idle" && <ArrowIcon />}
            </button>
            <p role="status" className="min-h-[20px] text-[14px] font-medium" style={{ color: status === "failed" ? "var(--sn-bad)" : "var(--sn-good)" }}>
              {status === "sent" ? k.sent[lang] : status === "failed" ? k.failed[lang] : ""}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
